/**
 * Admin-Self-Service-Inbox: Abwesenheiten, Probestunden-Anfragen, Kündigungen.
 * 3 Tabs in einer Seite, jeweils Liste + Aktionen.
 * Plusstunden liegen unter Honorare & Gehälter (/payroll?tab=plus-hours).
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, XCircle, Loader2, Inbox, CalendarPlus, FileSignature, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/axios';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import DateInput from '@/components/ui/date-input';
import { formatDate, formatDateTime, formatTime } from '@/lib/utils';
import { statusLabel } from '@/lib/deLabels';

const STATUS = {
  reported: 'bg-amber-100 text-amber-700',
  accepted: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-rose-100 text-rose-700',
  new: 'bg-blue-100 text-blue-700',
  contacted: 'bg-amber-100 text-amber-700',
  scheduled: 'bg-emerald-100 text-emerald-700',
  declined: 'bg-slate-200 text-slate-700',
  submitted: 'bg-amber-100 text-amber-700',
  approved: 'bg-emerald-100 text-emerald-700',
  processed: 'bg-emerald-100 text-emerald-700',
};

const SELF_SERVICE_TABS = new Set(['absences', 'trials', 'terminations']);

export default function AdminSelfServicePage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [summary, setSummary] = useState({
    open_absences: 0, new_trial_requests: 0, pending_terminations: 0,
  });

  // Alte Deep-Links: Plusstunden jetzt unter Honorare & Gehälter
  useEffect(() => {
    if (searchParams.get('tab') === 'plus-hours') {
      navigate('/payroll?tab=plus-hours', { replace: true });
    }
  }, [searchParams, navigate]);

  const activeTab = useMemo(() => {
    const tab = searchParams.get('tab');
    return SELF_SERVICE_TABS.has(tab) ? tab : 'absences';
  }, [searchParams]);

  const reloadSummary = useCallback(() => {
    api.get('/admin/self-service/summary').catch(() => ({ data: {} })).then((a) => {
      setSummary({
        open_absences: a.data?.open_absences || 0,
        new_trial_requests: a.data?.new_trial_requests || 0,
        pending_terminations: a.data?.pending_terminations || 0,
      });
    });
  }, []);
  useEffect(() => { reloadSummary(); }, [reloadSummary]);

  return (
    <Layout>
      <div className="p-6 md:p-8 space-y-4" data-testid="admin-self-service-page">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Inbox className="w-7 h-7 text-brand-red" /> Self-Service-Inbox
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Anträge aus Portal und Lehrkräfte-App: Abwesenheiten · Probestunden · Kündigungen
          </p>
        </div>

        <Tabs
          value={activeTab}
          onValueChange={(tab) => {
            const next = new URLSearchParams(searchParams);
            next.set('tab', tab);
            setSearchParams(next, { replace: true });
          }}
        >
          <TabsList>
            <TabsTrigger value="absences" data-testid="tab-absences">
              Abwesenheiten
              {summary.open_absences > 0 && <Badge className="ml-2 bg-rose-500 text-white">{summary.open_absences}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="trials" data-testid="tab-trials">
              Probestunden
              {summary.new_trial_requests > 0 && <Badge className="ml-2 bg-blue-500 text-white">{summary.new_trial_requests}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="terminations" data-testid="tab-terminations">
              Kündigungen
              {summary.pending_terminations > 0 && <Badge className="ml-2 bg-brand-red text-white">{summary.pending_terminations}</Badge>}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="absences" className="mt-4"><AbsencesTab onChange={reloadSummary} /></TabsContent>
          <TabsContent value="trials" className="mt-4"><TrialsTab onChange={reloadSummary} /></TabsContent>
          <TabsContent value="terminations" className="mt-4"><TerminationsTab onChange={reloadSummary} /></TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}

function AbsencesTab({ onChange }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [decision, setDecision] = useState(null); // {row, action}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/self-service/absences');
      setRows(r.data);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="bg-white border border-slate-200 rounded-xl">
      {loading ? <div className="p-6 text-slate-400"><Loader2 className="w-4 h-4 inline animate-spin" /> Laden…</div>
       : rows.length === 0 ? <div className="p-6 text-slate-400">Keine Meldungen</div> : (
        <table className="w-full text-sm" data-testid="absences-table">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="text-left px-4 py-3">Datum</th>
              <th className="text-left px-4 py-3">Schüler:in</th>
              <th className="text-left px-4 py-3">Grund</th>
              <th className="text-left px-4 py-3">Gemeldet von</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-right px-4 py-3">Aktion</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((r) => (
              <tr key={r.id} className="hover:bg-slate-50" data-testid={`absence-row-${r.id}`}>
                <td className="px-4 py-3">{r.absence_date}</td>
                <td className="px-4 py-3 font-medium">{r.student_name || r.student_id}</td>
                <td className="px-4 py-3 text-slate-600">{r.reason || '—'}</td>
                <td className="px-4 py-3 text-xs text-slate-500">{r.reported_by}</td>
                <td className="px-4 py-3"><Badge className={STATUS[r.status]}>{statusLabel(r.status)}</Badge></td>
                <td className="px-4 py-3 text-right">
                  {r.status === 'reported' ? (
                    <div className="flex gap-1 justify-end">
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => setDecision({ row: r, action: 'approve' })} data-testid={`absence-approve-${r.id}`}>
                        <CheckCircle2 className="w-3 h-3 mr-1" /> Bestätigen
                      </Button>
                      <Button size="sm" variant="outline" className="text-rose-600" onClick={() => setDecision({ row: r, action: 'reject' })}>
                        <XCircle className="w-3 h-3 mr-1" /> Ablehnen
                      </Button>
                    </div>
                  ) : <span className="text-xs text-slate-400">{r.responded_at ? formatDate(r.responded_at) : ''}</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {decision && <AbsenceDecisionDialog decision={decision} onClose={() => setDecision(null)} onDone={() => { setDecision(null); load(); onChange?.(); }} />}
    </div>
  );
}

function AbsenceDecisionDialog({ decision, onClose, onDone }) {
  const [notes, setNotes] = useState('');
  const [category, setCategory] = useState('entschuldigt abwesend');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/self-service/absences/${decision.row.id}/${decision.action}`, {
        teacher_notes: notes || null, category,
      });
      toast.success(decision.action === 'approve' ? 'Bestätigt + Ausfall eingetragen' : 'Abgelehnt');
      onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Aktion fehlgeschlagen'); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{decision.action === 'approve' ? 'Abwesenheit bestätigen' : 'Meldung ablehnen'}</DialogTitle>
          <DialogDescription>
            {decision.row.student_name} · {decision.row.absence_date}
            {decision.action === 'approve' && ' — bei Bestätigung wird automatisch ein Ausfall-Eintrag erzeugt.'}
          </DialogDescription>
        </DialogHeader>
        {decision.action === 'approve' && (
          <div>
            <label className="text-sm font-medium">Kategorie</label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="entschuldigt abwesend">Entschuldigt abwesend</SelectItem>
                <SelectItem value="krank">Krank</SelectItem>
                <SelectItem value="Ferien">Ferien</SelectItem>
                <SelectItem value="unentschuldigt">Unentschuldigt</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
        <Textarea rows={3} placeholder="Notiz an Eltern (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button onClick={submit} disabled={busy} className={decision.action === 'approve' ? 'bg-emerald-600 text-white' : 'bg-rose-600 text-white'}>
            {busy && <Loader2 className="w-4 h-4 animate-spin mr-1" />}
            {decision.action === 'approve' ? 'Bestätigen' : 'Ablehnen'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TrialsTab({ onChange }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows((await api.get('/admin/self-service/trial-lessons')).data); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="bg-white border border-slate-200 rounded-xl">
      {loading ? <div className="p-6 text-slate-400"><Loader2 className="w-4 h-4 inline animate-spin" /> Laden…</div>
       : rows.length === 0 ? <div className="p-6 text-slate-400">Keine Anfragen</div> : (
        <table className="w-full text-sm" data-testid="trials-table">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr><th className="text-left px-4 py-3">Datum</th><th className="text-left px-4 py-3">Kind</th><th className="text-left px-4 py-3">Fach</th><th className="text-left px-4 py-3">Kontakt</th><th className="text-left px-4 py-3">Wunsch-Termine</th><th className="text-left px-4 py-3">Status</th><th className="text-right px-4 py-3">Aktion</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((r) => (
              <tr key={r.id} className="hover:bg-slate-50" data-testid={`trial-row-${r.id}`}>
                <td className="px-4 py-3 text-xs">{formatDate(r.created_at)}</td>
                <td className="px-4 py-3"><strong>{r.student_first_name} {r.student_last_name}</strong>{r.student_age ? `, ${r.student_age} J.` : ''}</td>
                <td className="px-4 py-3">{r.subject_preference || '—'}</td>
                <td className="px-4 py-3 text-xs">{r.guardian_name}<br/>{r.guardian_email}<br/>{r.guardian_phone}</td>
                <td className="px-4 py-3 text-xs">{(r.preferred_times || []).map((t) => formatTime(t) || t).join(' · ') || '—'}</td>
                <td className="px-4 py-3"><Badge className={STATUS[r.status]}>{statusLabel(r.status)}</Badge></td>
                <td className="px-4 py-3 text-right">
                  <Button size="sm" variant="outline" onClick={() => setEditing(r)} data-testid={`trial-edit-${r.id}`}>Bearbeiten</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {editing && <TrialEditDialog row={editing} onClose={() => setEditing(null)} onDone={() => { setEditing(null); load(); onChange?.(); }} />}
    </div>
  );
}

function TrialEditDialog({ row, onClose, onDone }) {
  const [status, setStatus] = useState(row.status);
  const [notes, setNotes] = useState(row.admin_notes || '');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/self-service/trial-lessons/${row.id}`, { status, admin_notes: notes });
      toast.success('Aktualisiert'); onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen'); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{row.student_first_name} {row.student_last_name}</DialogTitle>
          <DialogDescription>Probestunden-Anfrage bearbeiten · {row.subject_preference || 'unbestimmtes Fach'}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          {row.experience && <div><strong>Vorerfahrung:</strong> {row.experience}</div>}
          {row.location_preference && <div><strong>Standort:</strong> {row.location_preference}</div>}
          <div>
            <label className="text-sm font-medium">Status</label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="trial-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="new">Neu eingegangen</SelectItem>
                <SelectItem value="contacted">Kontaktiert</SelectItem>
                <SelectItem value="scheduled">Probestunde geplant</SelectItem>
                <SelectItem value="declined">Abgelehnt</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Textarea rows={3} placeholder="Interne Notiz (sichtbar für Eltern)" value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button onClick={save} disabled={busy} className="bg-brand-red text-white" data-testid="trial-save">
            {busy && <Loader2 className="w-4 h-4 animate-spin mr-1" />} Speichern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TerminationsTab({ onChange }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [decision, setDecision] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows((await api.get('/admin/self-service/terminations')).data); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="bg-white border border-slate-200 rounded-xl">
      {loading ? <div className="p-6 text-slate-400"><Loader2 className="w-4 h-4 inline animate-spin" /> Laden…</div>
       : rows.length === 0 ? <div className="p-6 text-slate-400">Keine Anträge</div> : (
        <table className="w-full text-sm" data-testid="terminations-table">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr><th className="text-left px-4 py-3">Datum</th><th className="text-left px-4 py-3">Vertrag</th><th className="text-left px-4 py-3">Antragsteller:in</th><th className="text-left px-4 py-3">Frühestens</th><th className="text-left px-4 py-3">Wunsch</th><th className="text-left px-4 py-3">Unterschrift</th><th className="text-left px-4 py-3">Status</th><th className="text-right px-4 py-3">Aktion</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((r) => (
              <tr key={r.id} className="hover:bg-slate-50" data-testid={`termination-row-${r.id}`}>
                <td className="px-4 py-3 text-xs">{formatDate(r.created_at)}</td>
                <td className="px-4 py-3 font-medium">{r.contract_number}</td>
                <td className="px-4 py-3 text-xs">{r.requested_by_email}{r.reason && <><br /><em>"{r.reason}"</em></>}</td>
                <td className="px-4 py-3">{r.earliest_valid_date}</td>
                <td className="px-4 py-3">
                  {r.requested_termination_date}
                  {r.requested_termination_date < r.earliest_valid_date && (
                    <Badge className="ml-1 bg-amber-100 text-amber-700"><AlertTriangle className="w-3 h-3 mr-0.5" /> früher</Badge>
                  )}
                  {r.override_justification && <p className="text-xs text-brand-red mt-1">{r.override_justification}</p>}
                </td>
                <td className="px-4 py-3 text-xs italic">{r.signature_name}<br /><span className="text-slate-400">{formatDateTime(r.signed_at)}</span></td>
                <td className="px-4 py-3"><Badge className={STATUS[r.status]}>{statusLabel(r.status)}</Badge></td>
                <td className="px-4 py-3 text-right">
                  {r.status === 'submitted' ? (
                    <div className="flex gap-1 justify-end">
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => setDecision({ row: r, action: 'approve' })} data-testid={`termination-approve-${r.id}`}>
                        <CheckCircle2 className="w-3 h-3 mr-1" /> Annehmen
                      </Button>
                      <Button size="sm" variant="outline" className="text-rose-600" onClick={() => setDecision({ row: r, action: 'reject' })}>
                        <XCircle className="w-3 h-3 mr-1" /> Ablehnen
                      </Button>
                    </div>
                  ) : <span className="text-xs text-slate-400">{r.final_termination_date ? `→ ${r.final_termination_date}` : '—'}</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {decision && <TerminationDecisionDialog decision={decision} onClose={() => setDecision(null)} onDone={() => { setDecision(null); load(); onChange?.(); }} />}
    </div>
  );
}

function TerminationDecisionDialog({ decision, onClose, onDone }) {
  const [finalDate, setFinalDate] = useState(decision.row.requested_termination_date);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/self-service/terminations/${decision.row.id}/${decision.action}`, {
        final_termination_date: decision.action === 'approve' ? finalDate : null,
        admin_notes: notes || null,
      });
      toast.success(decision.action === 'approve' ? 'Kündigung angenommen + Vertrag aktualisiert' : 'Antrag abgelehnt');
      onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Aktion fehlgeschlagen'); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{decision.action === 'approve' ? 'Kündigung annehmen' : 'Kündigung ablehnen'}</DialogTitle>
          <DialogDescription>
            Vertrag {decision.row.contract_number} · Wunsch: {decision.row.requested_termination_date} · Frühestens: {decision.row.earliest_valid_date}
          </DialogDescription>
        </DialogHeader>
        {decision.action === 'approve' && (
          <div>
            <label className="text-sm font-medium">Endgültiges Vertragsende</label>
            <DateInput  value={finalDate} onChange={(e) => setFinalDate(e.target.value)} data-testid="termination-final-date" />
            <p className="text-xs text-slate-500 mt-1">Bei Annahme wird der Vertrag auf <code>terminated</code> gesetzt und das End-Datum hier eingetragen.</p>
          </div>
        )}
        <Textarea rows={3} placeholder="Notiz an Antragsteller:in (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button onClick={submit} disabled={busy} className={decision.action === 'approve' ? 'bg-emerald-600 text-white' : 'bg-rose-600 text-white'}>
            {busy && <Loader2 className="w-4 h-4 animate-spin mr-1" />}
            {decision.action === 'approve' ? 'Annehmen' : 'Ablehnen'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
